<template>
  <TableComp
    v-if="plans"
    name="Plans"
    :headers="headers"
    :status="plans.status"
    :pagination="plans.pagination"
    :sort="plans.sort"
    :number-of-items="plans.list.length"
    empty-image="plans"
    :empty-bold-message="$t('global.no-plans-bold-message')"
    :empty-message="$t('global.no-plans-message')"
    v-on:update:list="updateList"
  >
    <template #desktop>
      <tr v-for="plan in plans.list" v-bind:key="plan.id">
        <td>
          <div v-if="plan.isStable" class="d-flex align-items-center">
            <router-link :to="getRouterLink(plan)">{{ getName(plan) }}</router-link>
            <div class="pl-2">
              <Tooltip :content="getLocationTooltip(plan)">
                <Logo :logo="getLocationLogo(plan)" :size="getLogoSize()" />
              </Tooltip>
            </div>
          </div>
          <span v-else>{{ getName(plan) }}</span>
        </td>
        <td class="text-center">{{ getSitesUsed(plan) }}</td>
        <td class="text-center">
          <div class="d-flex justify-content-center">
            <Logo
              v-if="getAppEnvironment(plan)"
              :size="getLogoSize()"
              :logo="getAppEnvironment(plan)"
            />
          </div>
        </td>
        <td class="text-center">
          <div v-if="getType(plan)">
            <div>{{ getType(plan) }}</div>
            <div v-if="isFreeTrial(plan)" class="plan-free-trial">
              {{ $t("global.free-trial") }}
            </div>
          </div>
        </td>
        <td class="text-center">{{ getPrice(plan) }}</td>
        <td class="text-center" :class="{ 'plan-free-trial': isFreeTrial(plan) }">
          <div>{{ getRenewalDate(plan) }}</div>
          <div v-if="plan.cancellation" class="text-danger">
            <div>
              {{ $t("global.cancellation-pending") }}
            </div>
            <div>{{ getCancellationDate(plan) }}</div>
          </div>
        </td>
        <td class="text-end actions">
          <div v-if="plan.isStable" class="d-flex justify-content-end mr-4">
            <ThreeDotsMenu :items="menuItems(plan)" />
          </div>
          <div v-else class="d-flex justify-content-center align-items-center">
            <div class="mr-2">
              <loading-state :size="20" />
            </div>
            <div class="text-capitalize">{{ plan.state }}...</div>
          </div>
        </td>
      </tr>
    </template>
    <template #mobile>
      <TableMobileRow v-for="plan in plans.list" v-bind:key="plan.id">
        <template #header>
          <div class="d-flex align-items-center">
            <div class="flex-fill">
              <div v-if="plan.isStable" class="d-flex align-items-center">
                <router-link :to="getRouterLink(plan)">
                  {{ getName(plan) }}
                </router-link>
                <div class="pl-2">
                  <Tooltip :content="getLocationTooltip(plan)">
                    <Logo :logo="getLocationLogo(plan)" :size="getLogoSize()" />
                  </Tooltip>
                </div>
              </div>
              <span v-else>{{ getName(plan) }}</span>
            </div>
            <div>
              <div v-if="plan.isStable" class="d-flex justify-content-center">
                <ThreeDotsMenu :items="menuItems(plan)" />
              </div>
              <div v-else class="d-flex justify-content-center align-items-center">
                <div class="mr-2">
                  <loading-state :size="20" />
                </div>
                <div class="text-capitalize">{{ plan.state }}...</div>
              </div>
            </div>
          </div>
        </template>
        <template #content>
          <div class="row">
            <div class="col">{{ $t("global.sites-used") }}:</div>
            <div class="col">{{ getSitesUsed(plan) }}</div>
          </div>
          <div class="row">
            <div class="col">{{ $t("global.app-environment") }}:</div>
            <div class="col">
              <Logo
                v-if="getAppEnvironment(plan)"
                :size="getLogoSize()"
                :logo="getAppEnvironment(plan)"
              />
            </div>
          </div>
          <div class="row">
            <div class="col">{{ $t("global.type") }}:</div>
            <div class="col">
              <span v-if="getType(plan)">
                {{ getType(plan) }}
                <span v-if="isFreeTrial(plan)" class="plan-free-trial">
                  {{ $t("global.free-trial") }}
                </span>
              </span>
            </div>
          </div>
          <div class="row">
            <div class="col">{{ $t("global.price") }}:</div>
            <div class="col">{{ getPrice(plan) }}</div>
          </div>
          <div class="row">
            <div class="col">{{ $t("global.renewal-date") }}:</div>
            <div class="col" :class="{ 'plan-free-trial': isFreeTrial(plan) }">
              {{ getRenewalDate(plan) }}
            </div>
          </div>
        </template>
      </TableMobileRow>
    </template>
  </TableComp>
</template>

<script>
import { getContactSupport, getDeleteService } from "@/utils/serviceMenuOptions";
import { dateFormat } from "leatherman-js/utils/formatters/time";
import FreeTrialPlanUpgradeModal from "@/components/smart/modals/FreeTrialPlanUpgrade";
import LoadingState from "@/components/display/LoadingState";
import Logo from "@/components/display/Logo";
import { moneyFormat } from "leatherman-js/utils/formatters/money";
import NavLink from "@/classes/NavLink";
import PlanChangeName from "@/components/smart/modals/PlanChangeName";
import PlanChangeTerms from "@/components/smart/modals/PlanChangeTerms";
import PlanUpgrade from "@/components/smart/modals/PlanUpgrade";
import SiteAdd from "@/components/smart/modals/SiteAdd";
import SiteAddSecondary from "@/components/smart/modals/SiteAddSecondary";
import store from "@/store";
import TableComp from "@/components/display/table/TableComp";
import TableHeader from "@/classes/TableHeader";
import TableMobileRow from "@/components/display/table/TableMobileRow";
import ThreeDotsMenu from "@/components/display/ThreeDotsMenu";
import Tooltip from "@/components/display/Tooltip";

export default {
  name: "PlansTable",
  props: {
    plans: Object,
  },
  components: {
    LoadingState,
    Logo,
    TableComp,
    TableMobileRow,
    ThreeDotsMenu,
    Tooltip,
  },
  setup(props, { root }) {
    const headers = [
      new TableHeader(root.$t("global.name"), undefined),
      new TableHeader(root.$t("global.sites-used"), "justify-content-center"),
      new TableHeader(root.$t("global.app-environment"), "justify-content-center"),
      new TableHeader(root.$t("global.type"), "justify-content-center"),
      new TableHeader(root.$t("global.price"), "justify-content-center"),
      new TableHeader(root.$t("global.renewal-date"), "justify-content-center"),
      new TableHeader(),
    ];

    const menuItems = (plan) => {
      const canAddSite = (plan) => getSiteCount(plan) < getAllottedSites(plan);
      // const canAddDevEnvironment = (plan) =>
      //   plan.devEnvironmentCount < getAllottedDevEnvironments(plan) &&
      //   plan?.cloudAccount?.app?.is_developerable;
      const isLegacyWPWooPlan = (plan) =>
        getPackageLabel(plan)?.startsWith("wp.") || getPackageLabel(plan)?.startsWith("woo.");
      const canUpgradeToAnnual = (plan) =>
        !isFreeTrial(plan) &&
        !plan.isTShirt &&
        !plan.billing.isAnnualTerm &&
        !isLegacyWPWooPlan(plan);

      const items = [
        new NavLink(
          root.$t("global.plan-dashboard"),
          null,
          { name: "plan-detail", params: { planId: plan.id } },
          false,
          "default"
        ),
        new NavLink(
          root.$t("global.plan-upgrade"),
          null,
          () => {
            if (isFreeTrial(plan)) {
              store.dispatch("modals/open", {
                template: FreeTrialPlanUpgradeModal,
                data: {
                  package: plan.package,
                  plan: plan,
                  app: getApp(plan),
                },
              });
            } else {
              store.dispatch("modals/open", {
                template: PlanUpgrade,
                data: {
                  isPlanTShirt: plan.isTShirt,
                  package: getPackage(plan),
                  planId: plan.id,
                  planAppId: getAppId(plan),
                  planName: plan.nickname,
                  planSiteCount: plan.siteCount,
                  planTerm: plan.billing.months,
                },
              });
            }
          },
          false,
          "default",
          null
        ),
      ];

      if (canUpgradeToAnnual(plan)) {
        items.push(
          new NavLink(
            root.$t("global.plan-change-terms"),
            null,
            () => {
              store.dispatch("modals/open", {
                template: PlanChangeTerms,
                data: {
                  applicationName: getAppName(plan),
                  packageName: getPackageName(plan),
                  planId: plan.id,
                  planTerm: plan.billing.months,
                  pricing: getPackagePricing(plan),
                },
              });
            },
            false,
            "default",
            null
          )
        );
      }
      items.push(
        new NavLink(
          root.$t("global.plan-change-name"),
          null,
          () => {
            store.dispatch("modals/open", {
              template: PlanChangeName,
              data: {
                planId: plan.id,
                planName: plan.nickname,
              },
            });
          },
          false,
          "default",
          null
        )
      );
      if (canAddSite(plan)) {
        if (plan.isTShirt) {
          items.push(
            new NavLink(
              root.$t("global.site-add"),
              null,
              () => {
                store.dispatch("modals/open", {
                  template: SiteAddSecondary,
                  data: {
                    app: getApp(plan),
                    plan,
                    siteId: getPrimarySiteId(plan),
                  },
                });
              },
              false,
              "default",
              null
            )
          );
        } else {
          items.push(
            new NavLink(
              root.$t("global.site-add"),
              null,
              () => {
                store.dispatch("modals/open", {
                  template: SiteAdd,
                  data: { plan },
                });
              },
              false,
              "default",
              null
            )
          );
        }
      }
      items.push(getContactSupport(plan));
      const deleteServiceLink = getDeleteService(plan);
      if (deleteServiceLink) {
        items.push(deleteServiceLink);
      }
      return items;
    };

    const getAllottedSites = (plan) => getPackage(plan)?.allottedSites;
    const getApp = (plan) => plan?.app;
    const getAppEnvironment = (plan) => getApp(plan)?.type;
    const getAppId = (plan) => getApp(plan)?.id;
    const getAppName = (plan) => getApp(plan)?.name;
    // const getAllottedDevEnvironments = (plan) => getPackage(plan)?.devEnvironments;
    const getLocationLogo = (plan) => `location-${plan?.location?.country?.toLowerCase()}`;
    const getLocationTooltip = (plan) => `${plan?.location?.name} ${plan?.location?.country}`;
    const getLogoSize = () => 36;
    const getName = (plan) => plan.nickname;
    const getPackage = (plan) => plan?.package;
    const getPackageLabel = (plan) => getPackage(plan)?.label;
    const getPackageName = (plan) => getPackage(plan)?.name;
    const getPackagePricing = (plan) => getPackage(plan)?.pricing;
    const getPrice = (plan) =>
      `${moneyFormat(plan.billing.amount)} / ${plan.billing.abbreviatedFrequency}`;
    const getPrimarySite = (plan) => plan?.cloudAccount;
    const getPrimarySiteId = (plan) => getPrimarySite(plan)?.id;
    const getRenewalDate = (plan) =>
      isFreeTrial(plan)
        ? root.$t("global.plan-expiration", { date: plan.billing.formattedNextBillDate })
        : plan.billing.formattedNextBillDate;
    const getRouterLink = (plan) => ({ name: "plan-detail", params: { planId: plan.id } });
    const getSiteCount = (plan) => plan?.siteCount;
    const getSitesUsed = (plan) =>
      root.$t("global.plan-sites-used", {
        used: getSiteCount(plan),
        allotted: getAllottedSites(plan),
      });
    const getType = (plan) => getPackageName(plan);
    const getCancellationDate = (plan) => dateFormat(plan.cancellation.scheduledDate);
    const isFreeTrial = (plan) => plan?.package?.isFreeTrial;

    const updateList = (event) => {
      const { pagination, sort } = event;
      store.dispatch("plans/getList", {
        pageIndex: pagination?.index,
        pageSize: pagination?.size,
        sortProperty: sort?.property,
        sortDirection: sort?.direction,
      });
    };

    return {
      headers,
      menuItems,
      updateList,
      getAppEnvironment,
      getLocationLogo,
      getLocationTooltip,
      getLogoSize,
      getName,
      getPackage,
      getPrice,
      getPrimarySite,
      getRenewalDate,
      getRouterLink,
      getSitesUsed,
      getType,
      isFreeTrial,
      getCancellationDate,
    };
  },
};
</script>
